import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import {
  XMarkIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  ArrowUpOnSquareIcon,
  WrenchScrewdriverIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Spinner } from '../../../components';
import { CopilotMessage } from '../../../types';
import { useConversationStore, useUIStore } from '../../../stores';
import { useCopilotMessages } from '../hooks/useCopilotMessages';

export default function CopilotPanel() {
  const { selectedConversationId } = useConversationStore();
  const { copilotPanelOpen, setCopilotPanelOpen, setComposerDraft } = useUIStore();

  const { messages, isLoading, sendMessage, isSending } =
    useCopilotMessages(selectedConversationId);

  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isSending]);

  if (!copilotPanelOpen || !selectedConversationId) return null;

  const handleSubmit = () => {
    const text = input.trim();
    if (!text || isSending) return;
    sendMessage(text);
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleUse = (content: string) => {
    setComposerDraft(content);
    toast.success('Loaded into the customer composer — edit, then send');
  };

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col border-l border-line bg-surface shadow-l">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-brand" />
          <div>
            <h3 className="font-display text-sm font-semibold text-ink">AI Copilot</h3>
            <p className="text-xs text-ink-3">Private — the customer can't see this</p>
          </div>
        </div>
        <button
          onClick={() => setCopilotPanelOpen(false)}
          className="rounded-lg p-1.5 text-ink-2 hover:bg-black/[0.04]"
          title="Close copilot"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner text="Loading…" />
          </div>
        ) : messages.length === 0 ? (
          <div className="mt-10 px-2 text-center text-sm text-ink-3">
            {'Ask the AI anything about this customer — e.g. “show their open ' +
              'draft orders”, “add 2× <product> to their cart”, or “draft a ' +
              'reply about the refund”. Nothing here reaches the customer ' +
              'until you post it.'}
          </div>
        ) : (
          messages.map((m) => (
            <CopilotMessageItem key={m.id} message={m} onUse={handleUse} />
          ))
        )}
        {isSending && (
          <div className="flex items-center gap-2 text-xs text-ink-3">
            <Spinner />
            <span>AI is working…</span>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t border-line p-3">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask the AI…"
            rows={1}
            className="block w-full resize-none rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            style={{ maxHeight: '120px' }}
          />
          <button
            onClick={handleSubmit}
            disabled={!input.trim() || isSending}
            className="flex shrink-0 items-center justify-center rounded-xl bg-brand p-2.5 text-white hover:bg-brand/90 disabled:cursor-not-allowed disabled:opacity-50"
            title="Ask the AI"
          >
            <PaperAirplaneIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// The KB-write tool is a learning signal, not a routine lookup — surface it
// prominently so staff can see their correction was saved.
const KB_SAVE_TOOL = 'save_knowledge_correction';

function humanizeTool(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function CopilotMessageItem({
  message,
  onUse,
}: {
  message: CopilotMessage;
  onUse: (content: string) => void;
}) {
  const isStaff = message.role === 'staff';
  const savedToKb = message.toolSummary?.includes(KB_SAVE_TOOL);

  if (isStaff) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-card rounded-br-sm bg-brand px-3 py-2 text-sm text-white">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start">
      <div className="max-w-[90%] rounded-card rounded-bl-sm border border-brand/20 bg-brand/10 px-3 py-2">
        <div className="whitespace-pre-wrap text-sm text-ink">{message.content}</div>

        {savedToKb && (
          <div className="mt-2 flex items-center gap-1.5 rounded-card border border-success/30 bg-success/10 px-2 py-1 text-[11px] font-medium text-success">
            <BookOpenIcon className="h-3.5 w-3.5" />
            Saved to knowledge base — future answers will use this
          </div>
        )}

        {message.toolSummary && message.toolSummary.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1">
            <WrenchScrewdriverIcon className="h-3 w-3 text-ink-3" />
            {message.toolSummary
              .filter((t) => t !== KB_SAVE_TOOL)
              .map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-surface-2 px-2 py-0.5 text-[10px] font-medium text-ink-2"
                >
                  {humanizeTool(t)}
                </span>
              ))}
          </div>
        )}

        <button
          onClick={() => onUse(message.content)}
          className="mt-2 flex items-center gap-1 text-xs font-medium text-brand hover:text-brand/80"
          title="Load into the customer composer"
        >
          <ArrowUpOnSquareIcon className="h-3.5 w-3.5" />
          Use to customer
        </button>
      </div>
    </div>
  );
}
